import { createModel } from '@rematch/core';
import { PatientRx } from '../../models/patient-rx';
import { storeByIMPSId } from '../../utils/converter';
import { getPatientRxes } from '../../apis/patient-rx';

export interface State {
    allIds: number[];
    byId: Dictionary<PatientRx>;
    byMedId: Dictionary<PatientRx>;
    loading: Dictionary<boolean>;
}

export const patientRxes = createModel<State>({
    state: {
        allIds: [],
        byId: {},
        byMedId: {},
        loading: {}
    },
    reducers: {
        update: (
            state: State,
            data: Undefinable<MixType<PatientRx>>,
            loadingKey?: string
        ): State => {
            const byId = {
                ...state.byId,
                ...storeByIMPSId(data)
            };
            return {
                ...state,
                allIds: Object.keys(byId)
                    .sort((a, b) => byId[a]!.createdDate > byId[b]!.createdDate ? 1 : -1)
                    .map(x => +x),
                byId: byId,
                byMedId: {
                    ...state.byMedId,
                    ...processData(data)
                },
                ...loadingKey && {
                    loading: {
                        ...state.loading,
                        [loadingKey]: false
                    }
                }
            };
        },
        loading: (
            state: State,
            loadingKey: string,
            loading: boolean
        ): State => ({
            ...state,
            loading: {
                ...state.loading,
                [loadingKey]: loading
            }
        })
    },
    effects: dispatch => ({
        async getPatientRxesAsync() {
            const {
                compositeDrugs,
                dosageFrequencyTypes,
                dosageNumberTypes
            } = dispatch;
            this.loading('getPatientRxesAsync', true);
            try {
                const data = await getPatientRxes();
                this.update(data, 'getPatientRxesAsync');

                // Collect related data
                if (data) {
                    compositeDrugs.getCompositeDrugsAsync(data.map(x => x.medId));
                }
                dosageFrequencyTypes.getDosageFrequencyTypesAsync();
                dosageNumberTypes.getDosageNumberTypesAsync();
            } catch (e) {
                window.console.log(e);
                this.loading('getPatientRxesAsync', false);
            }
        }
    })
});

export const processData = (data: Undefinable<MixType<PatientRx>>): Dictionary<PatientRx> => {
    if (data) {
        if (Array.isArray(data)) {
            return data.reduce(
                (prev, x) => {
                    prev[x.medId] = x;
                    return prev;
                },
                {} as Dictionary<PatientRx>
            );
        } else {
            return {
                [data.medId]: data
            } as Dictionary<PatientRx>;
        }
    } else {
        return {} as Dictionary<PatientRx>;
    }
};
